import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionsService } from '../../core/permissions.service';
import { ListFilterService } from '../../core/list-filter.service';
import { NotificationService } from '../../core/notification.service';
import { SessionService } from '../../core/session.service';

@Component({
  standalone: false,
  selector: 'app-right-panel',
  templateUrl: './right-panel.component.html',
  styleUrls: ['./right-panel.component.scss']
})
export class RightPanelComponent implements OnInit {
  activeTab = 1; // 1=Filters, 2=Notifications, 3=Price Requests
  currentRoute = '';
  dateRangePreset = '';

  constructor(
    public perms: PermissionsService,
    public filterSvc: ListFilterService,
    public notifySvc: NotificationService,
    public session: SessionService,
    private router: Router
  ) {
    // Seed from the current URL so a hard reload sees the right context — the
    // initial NavigationEnd may fire before this component is constructed.
    this.currentRoute = this.router.url || '';
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: any) => {
      this.currentRoute = e.urlAfterRedirects || e.url || '';
      this.dateRangePreset = '';
    });
  }

  ngOnInit(): void {
    this.notifySvc.init();
    this.session.loadPriceRequests();
  }

  cancelPriceRequest(pr: any): void {
    this.session.cancelPriceRequest(pr.part_nr);
  }

  onNotificationClick(note: any): void {
    this.notifySvc.markAsRead(note);
    if (note.responselink) {
      this.router.navigate([note.responselink]);
    }
  }

  get showFilters(): boolean {
    return /\/(quotes|orders|products|rmas|files)/.test(this.currentRoute);
  }

  get filterContext(): string {
    if (this.currentRoute.includes('/quotes')) return 'quote';
    if (this.currentRoute.includes('/orders')) return 'order';
    if (this.currentRoute.includes('/products')) return 'products';
    if (this.currentRoute.includes('/rmas')) return 'rma';
    if (this.currentRoute.includes('/files')) return 'filestatus';
    return '';
  }

  get showDateRange(): boolean {
    return ['quote', 'order', 'rma'].includes(this.filterContext);
  }

  get dateField(): string {
    return this.filterContext === 'quote' ? 'insert_dt' : 'trandate';
  }

  onTextChange(value: string): void {
    this.filterSvc.setTextFilter(value);
  }

  onDateRangeChange(preset: string): void {
    this.dateRangePreset = preset;
    this.filterSvc.setDateRange(preset, this.dateField);
  }

  onStatusChange(value: string): void {
    this.filterSvc.setStatusFilter(value);
  }

  get statusOptions(): { value: string; label: string }[] {
    switch (this.filterContext) {
      case 'order':
        return [
          { value: '', label: 'All' },
          { value: 'Open', label: 'Open' },
          { value: 'Shipping', label: 'Shipping' },
          { value: 'Partial Invc', label: 'Partial Invc' },
          { value: 'Invoiced', label: 'Invoiced' },
          { value: 'Cancelled', label: 'Cancelled' }
        ];
      case 'quote':
        return [
          { value: '', label: 'All' },
          { value: '0', label: 'New' },
          ...(this.perms.catalogView ? [
            { value: '1', label: 'Price Requests Needed' },
            { value: '2', label: 'Price Request Submitted' },
            { value: '7', label: 'Price Request Denied' }
          ] : []),
          { value: '3', label: 'Ready to Buy' },
          { value: '4', label: 'Awaiting SO #' },
          { value: '5', label: 'Expired' }
        ];
      case 'rma':
        return [
          { value: '', label: 'All' },
          { value: 'Awaiting Product', label: 'Awaiting Product' },
          { value: 'Expired', label: 'Expired' },
          { value: 'rma not created', label: 'RMA not created' },
          { value: 'Vouchered', label: 'Vouchered' }
        ];
      case 'filestatus':
        return [
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'deleted', label: 'Deleted' }
        ];
      default:
        return [];
    }
  }
}
